import { getCssTextDecoration } from './tableCellLayoutUseCase';

/**
 * @typedef {import('@tanstack/react-table').Column<any, unknown>} LeafColumn
 */

/**
 * @typedef {object} LeafColumnsTable
 * @property {() => LeafColumn[]} getVisibleLeafColumns
 * @property {() => LeafColumn[]} [getLeftVisibleLeafColumns]
 * @property {() => LeafColumn[]} [getCenterVisibleLeafColumns]
 * @property {() => LeafColumn[]} [getRightVisibleLeafColumns]
 */

/**
 * Which corners of the thead rectangle a `<th>` occupies (for border-radius).
 *
 * Corners follow the **leaf column grid** (TanStack visible leaf order), not the index of `<th>`
 * within a `<tr>`. With rowspan on the left, the first `<th>` on the bottom row may start at
 * a leaf index after 0 — it must not receive bottom-left; that corner belongs to the cell that
 * actually spans from leaf 0.
 * @param {object} params
 * @param {number} params.rowIndex - Row index inside the built thead (0 = top)
 * @param {number} params.rowspan
 * @param {number} params.headRowCount - Number of `<tr>` rows in thead
 * @param {number} params.leafStartIndex - First leaf column covered by this cell (0-based)
 * @param {number} params.colspan - How many leaf columns this cell spans horizontally
 * @param {number} params.totalLeafCount - Total visible leaf columns (thead width in leaf units)
 * @returns {Array<'tl' | 'tr' | 'bl' | 'br'>}
 */
export function getTableHeadCornerKeys({
  rowIndex,
  rowspan,
  headRowCount,
  leafStartIndex,
  colspan,
  totalLeafCount,
}) {
  const lastRowIndex = headRowCount - 1;
  const leafEndExclusive = leafStartIndex + Math.max(1, colspan);

  const cellTouchesTopEdge = rowIndex === 0;
  const cellTouchesBottomEdge = rowIndex + rowspan > lastRowIndex;
  const cellTouchesLeftEdgeOfThead = leafStartIndex === 0;
  const cellTouchesRightEdgeOfThead = leafEndExclusive === totalLeafCount;

  /** @type {Array<'tl' | 'tr' | 'bl' | 'br'>} */
  const keys = [];
  if (cellTouchesTopEdge && cellTouchesLeftEdgeOfThead) keys.push('tl');
  if (cellTouchesTopEdge && cellTouchesRightEdgeOfThead) keys.push('tr');
  if (cellTouchesBottomEdge && cellTouchesLeftEdgeOfThead) keys.push('bl');
  if (cellTouchesBottomEdge && cellTouchesRightEdgeOfThead) keys.push('br');
  return keys;
}

/**
 * Inline styles derived from an API header cell (background, alignment, text decoration).
 * @param {ApiManagerTableColumnHeader} headerCell
 * @returns {React.CSSProperties}
 */
export function getApiHeaderCellInlineStyle(headerCell) {
  const bg = typeof headerCell.backgroundColor === 'string' && headerCell.backgroundColor.trim() !== ''
    ? headerCell.backgroundColor
    : undefined;
  const textDecoration = getCssTextDecoration(headerCell.textDecoration);
  return {
    ...(bg ? { backgroundColor: bg } : {}),
    textAlign: headerCell.align ?? 'left',
    ...(textDecoration ? { textDecoration } : {}),
  };
}

/**
 * Visible leaf columns in a pin-aware order (left, center, right).
 * Falls back to getVisibleLeafColumns for compatibility with partial table mocks.
 * @param {LeafColumnsTable} table
 * @returns {LeafColumn[]}
 */
export function getPinnedAwareVisibleLeafColumns(table) {
  const hasPinnedApis = typeof table.getLeftVisibleLeafColumns === 'function'
    && typeof table.getCenterVisibleLeafColumns === 'function'
    && typeof table.getRightVisibleLeafColumns === 'function';

  if (!hasPinnedApis) {
    return table.getVisibleLeafColumns();
  }

  return [
    ...table.getLeftVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getRightVisibleLeafColumns(),
  ];
}

/**
 * Width in px for a header cell: sum of TanStack leaf column sizes (aligns thead with tbody).
 * @param {LeafColumnsTable} table
 * @param {string} leaderColumnId
 * @param {number} colspan
 * @returns {number}
 */
export function getThSpanWidthPx(table, leaderColumnId, colspan) {
  const leaves = getPinnedAwareVisibleLeafColumns(table);
  const start = leaves.findIndex((c) => c.id === leaderColumnId);
  if (start < 0) return 100;
  const span = Math.max(1, colspan);
  let sum = 0;
  for (let j = 0; j < span && start + j < leaves.length; j += 1) {
    sum += leaves[start + j].getSize();
  }
  return sum || 100;
}
